<template>
    <div class="reassurances">
        <Title
            :title="content.title"
            :description="content.description"
        />
        <div class="vm">
            <div
                data-aos="fade-up"
                class="reassurance"
                v-for="(reassurance, index) in content.items"
                v-bind:key="index"
            >
                <!-- Inline SVG icon. Pink-tinted via currentColor on
                     the path so we can switch icons via the data file
                     without re-uploading anything to a CMS. Material
                     Icons paths (24x24 viewbox) used as the source. -->
                <svg
                    v-if="reassurance?.icon && iconPaths[reassurance.icon]"
                    class="reassurance__icon"
                    viewBox="0 0 24 24"
                    width="56"
                    height="56"
                    aria-hidden="true"
                >
                    <path :d="iconPaths[reassurance.icon]"
fill="currentColor" />
                </svg>
                <div class="title">
                    {{ reassurance?.title }}
                </div>
                <div class="description">
                    {{ reassurance?.description }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'
import { getReassurancesContent } from '@/data/reassurances'
import Title from '@/components/Text/Title.vue'

// Material-style icon paths (24x24 viewBox). Extracted as constants so
// the data file can reference them by name and stay copy-only. Add a
// new entry here, point at it from reassurances.js, done.
const ICON_PATHS = {
    // Solid heart. "care for the people who matter". the bienveillance
    // pillar. Brand callback. heart is also the Bzioup mark.
    favorite:
        'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
    // Clean crescent moon. "quiet, private, waiting". the privacy
    // pillar. (Was nights_stay. tiny "star" circles in that path read
    // as unfinished or dust in some sizes. Bedtime is a single
    // unambiguous shape.)
    bedtime:
        'M9.5 2c-1.82 0-3.53.5-5 1.35C7.59 5.08 9.5 8.5 9.5 12s-1.91 6.92-5 8.65C5.97 21.5 7.68 22 9.5 22c5.52 0 10-4.48 10-10S15.02 2 9.5 2z',
    // Verified badge (star with embedded check). "trusted, reliable".
    // the confiance/trust pillar. Reads as a guarantee mark, which
    // matches "Comptez sur nous" exactly.
    verified:
        'M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z',
}

export default {
    name: 'ReassurancesImage',
    components: {
        Title
    },
    // Prop kept for backward compat (Home.vue used to pass
    // data?.reassurances). Now ignored. content comes from local
    // data via the language store. Future cleanup: remove the prop
    // entirely once Home.vue stops passing it.
    props: {
        data: Object
    },
    data() {
        return {
            iconPaths: ICON_PATHS,
        }
    },
    computed: {
        ...mapState(useStore, ['language']),
        content() {
            return getReassurancesContent(this.language)
        },
    },
}
</script>

<style lang="scss" scoped>
.reassurances .vm {
    flex-wrap: wrap;
    gap: 18px;
    align-items: stretch;
    max-width: 960px;
    margin: 0 auto;
}

.reassurance {
    flex: 1 1 240px;
    max-width: 300px;
    padding: 30px 22px 28px;
    border-radius: 20px;
    background: var(--bzioup-bg-color-A);
    border: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: var(--bzioup-shadow-soft);
    transition: transform 220ms cubic-bezier(0.2, 0.8, 0.3, 1), box-shadow 220ms ease-out;

    .reassurance__icon {
        display: block;
        width: 34px;
        height: 34px;
        margin: 0 auto;
        padding: 15px;
        border-radius: 50%;
        color: var(--bzioup-color-pink);
        background: var(--bzioup-color-pink-soft);
    }

    .title {
        font-size: 19px;
        font-weight: 700;
        margin: 18px auto 0;
    }

    .description {
        font-size: 15px;
        line-height: 1.5;
        margin: 8px auto 0;
        color: var(--bzioup-color-Y);
        white-space: pre-line;
    }
}

.reassurance:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(8, 20, 72, 0.1);
}

.reassurance:hover .reassurance__icon {
    animation: reassurance-wiggle 600ms ease-in-out;
}

@keyframes reassurance-wiggle {
    0%, 100% { transform: rotate(0); }
    25%      { transform: rotate(-10deg) scale(1.08); }
    75%      { transform: rotate(10deg) scale(1.08); }
}
</style>
